import { createFileRoute, notFound } from "@tanstack/react-router";
import { cityFromSlug, specialtyFromSlug } from "@/lib/seo-slugs";
import { getIntentProviders } from "@/lib/seo-intent.functions";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { IntentPageShell } from "@/components/seo/IntentPageShell";
import { setSeoPageCache } from "@/lib/page-cache";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialty/$specialty/$city/available-today")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setSeoPageCache();
 const specialty = specialtyFromSlug(params.specialty);
 const city = cityFromSlug(params.city);
 if (!specialty || !city) throw notFound();
 const cityName = city.split(",")[0].trim();
 const state = (city.split(",")[1] ?? "").trim();
 const { providers, total } = await getIntentProviders({
 data: { specialty, city: cityName, state: state || undefined, kind: "available_today" },
 });
 return { specialty, city, cityName, state, providers, total };
 },
 head: ({ params, loaderData }) => {
 const specialty = loaderData?.specialty ?? "Providers";
 const city = loaderData?.city ?? "";
 const total = loaderData?.total ?? 0;
 const url = `${SITE}/specialty/${params.specialty}/${params.city}/available-today`;
 const baseUrl = `${SITE}/specialty/${params.specialty}/${params.city}`;
 const title = `${specialty} available today in ${city} | Heallexa`;
 const description = `Same-day ${specialty.toLowerCase()} appointments in ${city}. Compare providers with open availability today, check insurance, and book online — free, no signup required, on Heallexa.`;
 const robots = decideIndexability({
 providerCount: total,
 hasFaq: false,
 hasNearbyCities: false,
 internalLinkCount: 1,
 hasRelatedProviders: total > 0,
 });
 const canonical = robots.index ? url : baseUrl;
 return {
 meta: [
 { title }, { name: "description", content: description },
 { property: "og:title", content: title }, { property: "og:description", content: description },
 { property: "og:url", content: canonical }, robotsMeta(robots.robots),
 ],
 links: [{ rel: "canonical", href: canonical }],
 };
 },
 component: () => {
 const { specialty, city, cityName, providers } = Route.useLoaderData();
 return (
 <IntentPageShell
 title={`${specialty} available today in ${city}`}
 description={`Same-day ${specialty.toLowerCase()} in ${city}.`}
 h1={`${specialty} available today in ${cityName}`}
 intro={`Providers with open availability today in ${city}. We only list ${specialty.toLowerCase()} who actively manage their calendar on Heallexa.`}
 emptyMessage={`No ${specialty.toLowerCase()} in ${city} have published today's availability yet. Browse the full ${specialty.toLowerCase()} directory instead.`}
 providers={providers}
 breadcrumbs={[
 { label: "Heallexa", href: "/" },
 { label: specialty, href: `/specialty/${(specialty).toLowerCase().replace(/[^a-z0-9]+/g, "-")}` },
 { label: cityName, href: `/specialty/${(specialty).toLowerCase().replace(/[^a-z0-9]+/g, "-")}/${cityName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` },
 { label: "Available today" },
 ]}
 />
 );
 },
});
